import React, { PropsWithChildren, useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';

import CaretDown from 'svg/CaretDown';
import CaretRight from 'svg/CaretRight';

const outerStyles = css`
  border: 2px solid transparent;
  &.active {
    border-left-color: ${({ theme }) => theme.colors.white};
    background-color: ${({ theme }) => theme.colors.white15};
  }
`;

const colorChangeStyles = css`
  color: ${({ theme }) => theme.colors.white75};
  font-family: 'Lato';
  text-decoration: none;
  font-size: 14px;
  line-height: 16px;
  &.active {
    color: ${({ theme }) => theme.colors.white};
    font-weight: 700;

    & svg {
      opacity: 1;
    }
  }
`;

const menuItemStyles = css`
  align-items: center;
  cursor: pointer;
  display: flex;
  padding: 14px;

  & > svg,
  & > div {
    margin-right: 10px;
    opacity: 0.5;
  }
`;

const StyledNavLink = styled(NavLink).attrs({
  draggable: false,
})`
  ${colorChangeStyles}
  ${menuItemStyles}
  ${outerStyles}
`;

const StyledSubNavLink = styled(NavLink).attrs({
  draggable: false,
})`
  ${colorChangeStyles}
  ${menuItemStyles}
  font-weight: normal;
`;

const ExpandedNavLink = styled.div`
  ${menuItemStyles}
`;

const StyledNavLinkContainer = styled.div`
  ${colorChangeStyles}
  ${outerStyles}
  display: flex;
  flex-direction: column;
`;

const FlexSpacer = styled.div`
  flex: 1;
`;

type Props = PropsWithChildren<{
  icon?: React.JSX.Element;
  title: string;
  domain?: string;
  to: string | { subtitle: string; to: string }[];
  visible?: boolean;
  end?: boolean;
  'data-cy'?: string;
}>;

function SideNavLink({ icon, title = '', to = '', visible = true, domain, end = true, ...props }: Props) {
  const [expanded, setExpanded] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const isActiveInArray =
    (typeof to === 'string' && location.pathname.startsWith(to)) ||
    (Array.isArray(to) && to.some((item) => location.pathname.startsWith(item.to)));
  useEffect(() => {
    if (isActiveInArray && !expanded) {
      setExpanded(true);
    }
    if (!isActiveInArray && expanded) {
      setExpanded(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActiveInArray]);

  const handleOuterClick = () => {
    if (isActiveInArray) {
      setExpanded(!expanded);
    } else {
      navigate((to[0] as { to: string }).to);
    }
  };
  const handleCaretClick = (event: React.MouseEvent<SVGSVGElement>) => {
    event.stopPropagation();
    setExpanded(!expanded);
  };
  return (
    visible &&
    (Array.isArray(to) ? (
      <StyledNavLinkContainer className={isActiveInArray ? 'active' : ''} {...props}>
        <ExpandedNavLink onClick={handleOuterClick}>
          {icon ? icon : <Spacer />}
          <span className='link-title'>{title}</span>
          <FlexSpacer />
          {expanded ? <CaretDown onClick={handleCaretClick} /> : <CaretRight onClick={handleCaretClick} />}
        </ExpandedNavLink>
        {expanded &&
          to.map((item, i) => (
            <StyledSubNavLink to={item.to} key={i} end>
              <Spacer />
              <span className='link-title'>{item.subtitle}</span>
            </StyledSubNavLink>
          ))}
      </StyledNavLinkContainer>
    ) : (
      <StyledNavLink to={domain ? `${domain}${to}` : to} {...(domain ? { target: '_blank' } : {})} end={end} {...props}>
        {icon ? icon : <Spacer />}
        <span className='link-title'>{title}</span>
      </StyledNavLink>
    ))
  );
}

export default SideNavLink;

const Spacer = styled.div`
  width: 18px;
  height: 18px;
  display: inline-block;
`;
